<template>
  <div>
    <!-- Hero Section -->
    <section
      id="accueil"
      class="relative text-white pt-16 min-h-[26rem] md:h-[32rem] flex items-center"
    >
      <!-- Background Image -->
      <div class="slider absolute inset-0 bg-cover bg-center bg-no-repeat">
        <div class="slides">
          <img src="/assets/img/slide-two_.webp" alt="Baie d'Oran" />
          <img src="/assets/img/slide-three_.webp" alt="Lions d'Oran" />
        </div>
      </div>
      <!-- Dark overlay for better text readability -->
      <div class="bo bg-black bg-opacity-30"></div>

      <!-- Content -->
      <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div class="text-center">
          <h1
            class="text-4xl md:text-6xl font-bold mb-6 drop-shadow-lg animate-fade-in-up"
          >
            <span
              class="inline-block animate-bounce-in"
              style="animation-delay: 0.2s"
              >Association</span
            >&nbsp;
            <span
              class="inline-block animate-bounce-in"
              style="animation-delay: 0.4s"
              >des</span
            >&nbsp;
            <span
              class="inline-block animate-bounce-in"
              style="animation-delay: 0.6s"
              >Médecins</span
            >&nbsp;
            <span
              class="inline-block animate-bounce-in"
              style="animation-delay: 0.8s"
              >Généralistes</span
            >&nbsp;
            <span
              class="inline-block animate-bounce-in"
              style="animation-delay: 1s"
              >d'Oran</span
            >
          </h1>
          <div
            class="text-lg md:text-2xl mb-8 max-w-3xl mx-auto opacity-90 drop-shadow-md"
          >
            Promouvoir l'excellence médicale et renforcer la solidarité
            professionnelle des médecins de famille
          </div>
          <div class="flex flex-wrap justify-center gap-4">
            <a
              href="#a_propos"
              class="bg-white text-indigo-600 px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block shadow-lg"
            >
              Découvrir
            </a>
            <a
              href="#nousrejoindre"
              class="border-2 border-white text-white px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-indigo-600 transition-colors inline-block shadow-lg"
            >
              Nous rejoindre
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Annonces -->
    <section v-if="announcements.length > 0" class="pt-20 pb-5 bg-white">
      <div class="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
        <div class="space-y-10">
          <article
            v-for="item in announcements"
            :key="item.id"
            class="bg-gray-50 rounded-2xl p-2 sm:p-8 border border-gray-200"
          >
            <div class="text-center mb-8">
              <div v-if="item.icon" class="text-5xl mb-3">{{ item.icon }}</div>
              <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {{ item.title }}
              </h2>
              <div
                class="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0 md:space-x-8 text-lg text-gray-700"
              >
                <div v-if="item.lieu" class="flex items-center">
                  <span class="text-2xl mr-2">📍</span>
                  <span class="font-semibold">{{ item.lieu }}</span>
                </div>
                <div v-if="item.date" class="flex items-center">
                  <span class="text-2xl mr-2">🗓</span>
                  <span class="font-semibold">{{ item.date }}</span>
                </div>
              </div>
            </div>

            <div
              class="annonce-content text-sm leading-relaxed text-gray-700"
              v-html="item.description"
            ></div>
          </article>
        </div>
      </div>
    </section>
    <!-- Fin Annonces -->
    <!-- À propos -->
    <section id="a_propos" class="py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            À propos d'AMGO
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Fondée avec l'ambition de promouvoir l'excellence médicale et de
            renforcer la solidarité professionnelle
          </p>
        </div>

        <div class="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h3 class="text-2xl font-bold text-gray-900 mb-6">
              Notre Mission & Vision
            </h3>
            <p class="text-gray-600 mb-6 leading-relaxed">
              L'Association des Médecins Généralistes - Médecins de Famille
              d'Oran (AMGO) est une institution dynamique et visionnaire créée
              par des médecins engagés issus des secteurs public, libéral et
              para-public de la wilaya d'Oran.
            </p>
            <p class="text-gray-600 mb-6 leading-relaxed">
              Notre mission transcende les frontières de la médecine générale.
              Nous œuvrons à développer un modèle de soins primaires basé sur
              l'excellence scientifique, l'éthique et l'engagement
              communautaire.
            </p>
            <div class="flex items-center space-x-4">
              <div class="bg-indigo-100 p-3 rounded-full">
                <svg
                  class="w-6 h-6 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  ></path>
                </svg>
              </div>
              <span class="text-lg font-semibold text-gray-900"
                >Médecins de famille unis pour l'excellence</span
              >
            </div>
          </div>
          <div
            class="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 sm:p-8 rounded-2xl"
          >
            <h3 class="text-2xl font-bold text-gray-900 mb-6">
              Nos Valeurs Fondatrices
            </h3>
            <div class="space-y-4">
              <div class="flex items-start space-x-3">
                <div class="bg-indigo-600 w-2 h-2 rounded-full mt-2"></div>
                <div>
                  <h4 class="font-semibold text-gray-900">
                    Excellence médicale
                  </h4>
                  <p class="text-gray-600 text-sm">
                    Une quête perpétuelle de qualité dans nos actions et nos
                    services
                  </p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="bg-indigo-600 w-2 h-2 rounded-full mt-2"></div>
                <div>
                  <h4 class="font-semibold text-gray-900">
                    Respect de la dignité humaine
                  </h4>
                  <p class="text-gray-600 text-sm">
                    L'éthique et la valeur humaine au cœur de notre démarche
                  </p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="bg-indigo-600 w-2 h-2 rounded-full mt-2"></div>
                <div>
                  <h4 class="font-semibold text-gray-900">
                    Solidarité et partage
                  </h4>
                  <p class="text-gray-600 text-sm">
                    Un esprit collectif où le soutien mutuel définit notre
                    travail
                  </p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="bg-indigo-600 w-2 h-2 rounded-full mt-2"></div>
                <div>
                  <h4 class="font-semibold text-gray-900">
                    Innovation médicale
                  </h4>
                  <p class="text-gray-600 text-sm">
                    Recherche et diffusion des innovations en médecine générale
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Activités -->
    <section id="nosobjectifs" class="py-20 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Nos Objectifs Stratégiques
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Une contribution durable à la médecine générale et aux soins
            primaires
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
          <div
            id="FormationContinue"
            class="bg-white rounded-xl shadow-lg overflow-hidden card-hover"
          >
            <div class="bg-gradient-to-r from-blue-500 to-blue-600 p-6">
              <div class="text-white text-center">
                <div class="text-4xl mb-2">🎓</div>
                <h3 class="text-xl font-bold">
                  Perfectionnement des Compétences
                </h3>
              </div>
            </div>
            <div class="p-6">
              <p class="text-gray-600 mb-4">
                AMGO est un catalyseur de savoirs, organisant des congrès
                scientifiques, ateliers pratiques, et colloques d'envergure
                nationale pour renforcer les capacités des praticiens.
              </p>
              <div class="text-sm text-indigo-600 font-semibold">
                Formation continue • Congrès nationaux
              </div>
            </div>
          </div>

          <div
            id="RechercheMedicale"
            class="bg-white rounded-xl shadow-lg overflow-hidden card-hover"
          >
            <div class="bg-gradient-to-r from-green-500 to-green-600 p-6">
              <div class="text-white text-center">
                <div class="text-4xl mb-2">🔬</div>
                <h3 class="text-xl font-bold">Recherche et Publication</h3>
              </div>
            </div>
            <div class="p-6">
              <p class="text-gray-600 mb-4">
                Nous encourageons activement la recherche médicale, la diffusion
                des innovations et la publication des travaux, positionnant Oran
                comme un centre d'excellence médicale.
              </p>
              <div class="text-sm text-indigo-600 font-semibold">
                Recherche active • Publications scientifiques
              </div>
            </div>
          </div>

          <div
            id="EngagementCommunautaire"
            class="bg-white rounded-xl shadow-lg overflow-hidden card-hover"
          >
            <div class="bg-gradient-to-r from-purple-500 to-purple-600 p-6">
              <div class="text-white text-center">
                <div class="text-4xl mb-2">🏥</div>
                <h3 class="text-xl font-bold">Engagement Communautaire</h3>
              </div>
            </div>
            <div class="p-6">
              <p class="text-gray-600 mb-4">
                Par des actions de sensibilisation et d'éducation, nous
                adressons des problématiques majeures comme les fléaux sociaux
                et les défis de santé publique.
              </p>
              <div class="text-sm text-indigo-600 font-semibold">
                Sensibilisation • Éducation sanitaire
              </div>
            </div>
          </div>

          <div
            id="SolidariteProfessionnelle"
            class="bg-white rounded-xl shadow-lg overflow-hidden card-hover"
          >
            <div class="bg-gradient-to-r from-orange-500 to-orange-600 p-6">
              <div class="text-white text-center">
                <div class="text-4xl mb-2">🤝</div>
                <h3 class="text-xl font-bold">Solidarité et Réseau</h3>
              </div>
            </div>
            <div class="p-6">
              <p class="text-gray-600 mb-4">
                AMGO favorise une coopération harmonieuse entre les médecins au
                niveau local, régional et national, tout en renforçant les
                synergies pour faire face aux défis sanitaires.
              </p>
              <div class="text-sm text-indigo-600 font-semibold">
                Réseau médical • Coopération nationale
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Événements -->
    <Actualites />

    <!-- Contact -->
    <Contact />

    <!-- Déclaration officielle -->
    <section
      class="py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white"
    >
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold mb-4">
            Déclaration Officielle AMGO
          </h2>
          <p class="text-xl opacity-90 max-w-3xl mx-auto">
            Publications et vocation de l'Association des Médecins Généralistes
            d'Oran
          </p>
        </div>

        <div class="grid lg:grid-cols-2 gap-12">
          <!-- Version française -->
          <div class="bg-white bg-opacity-10 rounded-2xl p-4 sm:p-8 backdrop-blur-sm">
            <div class="flex items-center mb-6">
              <div class="bg-blue-600 p-3 rounded-full mr-4">
                <svg
                  class="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  ></path>
                </svg>
              </div>
              <h3 class="text-2xl font-bold">Déclaration officielle</h3>
            </div>

            <div class="space-y-4 text-sm leading-relaxed">
              <p class="font-semibold text-blue-200">
                À l'attention de nos confrères, consœurs et du public éclairé,
              </p>

              <p>
                Il nous parvient, de manière récurrente, des demandes visant à
                publier sur nos canaux officiels des lettres personnelles,
                affiches privées ou contenus relevant d'initiatives
                individuelles. Bien que nous respections pleinement la liberté
                d'expression et les moyens de communication propres à chacun —
                qu'il s'agisse de pages personnelles, de groupes autonomes ou de
                réseaux sociaux — il convient de rappeler que chacun demeure
                seul responsable de la gestion et de la diffusion de ses propres
                contenus.
              </p>

              <p>
                Notre association, l'AMGO, est fondée sur une
                <strong
                  >vocation strictement scientifique, médicale et
                  pédagogique</strong
                >. Elle est dédiée à la médecine générale dans son essence
                académique, clinique et éthique.
              </p>

              <div class="bg-green-600 bg-opacity-20 p-4 rounded-lg">
                <p class="font-semibold mb-2">
                  À ce titre, nous accueillons avec honneur et rigueur :
                </p>
                <ul class="list-disc list-inside space-y-1 text-sm">
                  <li>Les travaux de recherche</li>
                  <li>Les publications scientifiques</li>
                  <li>
                    Les annonces de journées d'étude, colloques et congrès
                    médicaux
                  </li>
                  <li>
                    Toute initiative contribuant à l'élévation du savoir médical
                  </li>
                </ul>
              </div>

              <div class="bg-red-600 bg-opacity-20 p-4 rounded-lg">
                <p class="font-semibold mb-2">En revanche, nous ne sommes :</p>
                <ul class="list-disc list-inside space-y-1 text-sm">
                  <li>Ni un groupe de revendication</li>
                  <li>Ni un syndicat professionnel</li>
                  <li>Ni un parti politique</li>
                  <li>Ni une association caritative ou religieuse</li>
                  <li>Ni un espace de publicité personnelle ou commerciale</li>
                </ul>
              </div>

              <p>
                Notre engagement est exclusivement tourné vers la valorisation
                du savoir médical, la diffusion scientifique et le renforcement
                de la pédagogie clinique.
              </p>

              <p class="font-semibold text-blue-200">
                Avec respect et engagement,<br />
                AMGO - Porte-voix de la rigueur, de la pédagogie et de la
                dignité professionnelle.
              </p>
            </div>
          </div>

          <!-- Version arabe -->
          <div
            class="bg-white bg-opacity-10 rounded-2xl p-4 sm:p-8 backdrop-blur-sm"
            dir="rtl"
          >
            <div class="flex items-center mb-6 justify-end">
              <h3 class="text-2xl font-bold ml-4">بيان توضيحي</h3>
              <div class="bg-green-600 p-3 rounded-full">
                <svg
                  class="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  ></path>
                </svg>
              </div>
            </div>

            <div class="space-y-4 text-sm leading-relaxed">
              <p class="font-semibold text-green-200">
                أيها الزملاء والزميلات، أيها الجمهور الكريم،
              </p>

              <p>
                لقد أصبح من المعتاد أن تصلنا طلبات لنشر رسائل شخصية، أو ملصقات
                خاصة، أو إعلانات فردية عبر قنواتنا الرسمية. ونحن إذ نحترم حرية
                التعبير وحق كل شخص في إدارة صفحته الخاصة أو مجموعته الافتراضية،
                نود أن نذكّر بلطف أن كلٌ مسؤولٌ عن محتواه.
              </p>

              <p>
                جمعيتنا، ولله الحمد، ليست وكالة أنباء، ولا جريدة حائط، ولا منصة
                إعلانات تجارية أو شخصية.
                <strong>نحن جمعية علمية طبية بامتياز</strong>، لا نبيع ولا
                نشتري، ولا نروّج إلا للعلم والمعرفة.
              </p>

              <div class="bg-green-600 bg-opacity-20 p-4 rounded-lg">
                <p class="font-semibold mb-2">نرحب بكل ما هو:</p>
                <ul class="list-disc list-inside space-y-1 text-sm">
                  <li>بحث علمي رصين</li>
                  <li>منشور أكاديمي موثّق</li>
                  <li>إعلان عن يوم دراسي أو مؤتمر طبي</li>
                  <li>مساهمة ترفع من شأن الطب العام</li>
                </ul>
              </div>

              <div class="bg-red-600 bg-opacity-20 p-4 rounded-lg">
                <p class="font-semibold mb-2">
                  أما ما عدا ذلك، فنعتذر بكل احترام. فنحن:
                </p>
                <ul class="list-disc list-inside space-y-1 text-sm">
                  <li>لسنا نقابة</li>
                  <li>ولسنا حزباً سياسياً</li>
                  <li>ولسنا جمعية خيرية أو دينية</li>
                  <li>ولسنا وكالة دعاية أو تسويق</li>
                  <li>ولسنا منصة لتلميع الصور</li>
                </ul>
              </div>

              <p>
                مع خالص الاحترام،<br />
                <span class="font-semibold text-green-200"
                  >جمعية الأطباء العامين أطباء الأسرة وهران AMGO<br />
                  صوت الطب، وراية المهنية، ودرع الكرامة الأكاديمية</span
                >
              </p>
            </div>
          </div>
        </div>

        <div class="text-center mt-12">
          <div
            class="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm max-w-2xl mx-auto"
          >
            <div class="flex items-center justify-center mb-4">
              <div class="bg-indigo-600 p-2 rounded-full mr-3">
                <svg
                  class="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
              </div>
              <h4 class="text-lg font-semibold">Message Important</h4>
            </div>
            <p class="text-sm opacity-90">
              Cette déclaration reflète notre engagement envers l'excellence
              scientifique et notre respect pour l'intégrité de notre mission
              médicale et pédagogique.
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Actualites from "./Actualites.vue";
import Contact from "./Contact.vue";

export default {
  name: "Body",
  props: {},
  components: {
    Contact,
    Actualites,
  },
  data() {
    return {
      announcements: [],
    };
  },
  mounted() {
    this.fetchAnnouncements();
  },
  methods: {
    async fetchAnnouncements() {
      try {
        const res = await fetch(
          `${process.env.VUE_APP_HSERVER_URL}/api/actualites/announcements`,
          { headers: { "Content-Type": "application/json" } },
        );
        if (res.ok) {
          this.announcements = await res.json();
        }
      } catch (err) {
        this.announcements = [];
      }
    },
  },
};
</script>
